import { Order, AddonTask } from '@spike/order-model';
import Pet from '@spike/pet-model';
import { Task } from '@spike/task-model';
import clsx from 'clsx';
import styles from 'components/OrderDetail/Tabs/Tasks/OrderDetailTasksTab.module.scss';
import { TaskDetail } from 'components/Tasks/Detail/TaskDetail';
import { PetProfileCard } from 'components/UI/V3/PetProfileCard/PetProfileCard';
import { PetTagTabs } from 'components/UI/V3/PetTag/Tabs/PetTagTabs';
import React, { useMemo, useState } from 'react';
import { Tabs } from 'components/UI/V2/Tabs/Tabs';
import { status } from '@spike/appointment-model';
import Button from 'components/UI/V3/Button/Button';
import TaskComponent from 'components/Tasks/Task/TaskComponent';
import { TaskTypeSelector } from 'components/Tasks/TaskTypeSelector/TaskTypeSelector';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationDot } from '@fortawesome/pro-solid-svg-icons';
import { Title } from 'components/Checkout2/UI/Title';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';

interface OrderDetailTasksTabProps {
    order?: Order;
    pets: Array<Pet>;
    className?: string;
    readOnly?: boolean;
}

type TaskFilterType = 'upcoming' | 'past-due' | 'completed';

const isTaskCancelled = (appointmentStatus?: number) =>
    appointmentStatus === status.CANCELLED || appointmentStatus === status.NO_SHOW;

const filterAddonTaskByType = (task: AddonTask, filter: TaskFilterType): boolean => {
    if (isTaskCancelled(task.appointmentStatus)) return false;

    switch (filter) {
        case 'upcoming':
            return !task.completed && !task.overdue;
        case 'past-due':
            return task.overdue;
        case 'completed':
            return !!task.completed;
        default:
            return false;
    }
};

const filterTaskByType = (task: Task, filter: TaskFilterType): boolean => {
    if (isTaskCancelled(task.appointmentStatus)) return false;

    switch (filter) {
        case 'upcoming':
            return !task.completed && !task.overdue;
        case 'past-due':
            return !!task.overdue;
        case 'completed':
            return !!task.completed;
        default:
            return false;
    }
};

export const OrderDetailTasksTab: React.FC<OrderDetailTasksTabProps> = ({ order, pets, className, readOnly }) => {
    const [selectedPet, setSelectedPet] = useState<Pet>(pets[0]);
    const [selectedFilter, setSelectedFilter] = useState<TaskFilterType>('upcoming');
    const [showTaskTypeSelector, setShowTaskTypeSelector] = useState(false);
    const [showTaskComponent, setShowTaskComponent] = useState(false);
    const [selectedTaskType, setSelectedTaskType] = useState<string | undefined>(undefined);
    const [taskToEdit, setTaskToEdit] = useState<Task | undefined>(undefined);
    const [taskModalKey, setTaskModalKey] = useState(0);
    const isReadOnly = readOnly ?? false;
    const { getOrganizationMarketplace, organizationMarketplaces } = useOrganizationMarketplaces();

    const tasksByPet = useMemo(() => {
        const tasksMap = new Map<number, Task[]>();

        order?.items?.appointments?.forEach(item => {
            if (item.appointment) {
                const petId = item.appointment.pet.id;
                const tasks = item.appointment.tasks || [];

                if (!tasksMap.has(petId)) {
                    tasksMap.set(petId, []);
                }
                tasksMap.get(petId)!.push(...tasks);
            }
        });

        return tasksMap;
    }, [order]);

    const addonTasksByPet = useMemo(() => {
        const tasksMap = new Map<number, AddonTask[]>();

        order?.addonTasks?.forEach(task => {
            if (!tasksMap.has(task.petId)) {
                tasksMap.set(task.petId, []);
            }
            tasksMap.get(task.petId)!.push(task);
        });

        return tasksMap;
    }, [order]);

    const selectedPetTasks = useMemo(() => {
        return tasksByPet.get(selectedPet.id!) || [];
    }, [tasksByPet, selectedPet]);

    const selectedPetAddonTasks = useMemo(() => {
        return addonTasksByPet.get(selectedPet.id!) || [];
    }, [addonTasksByPet, selectedPet]);

    const hasAnyTasks = selectedPetTasks.length > 0 || selectedPetAddonTasks.length > 0;

    const taskCounts = useMemo(() => {
        const upcoming =
            selectedPetTasks.filter(task => filterTaskByType(task, 'upcoming')).length +
            selectedPetAddonTasks.filter(task => filterAddonTaskByType(task, 'upcoming')).length;

        const pastDue =
            selectedPetTasks.filter(task => filterTaskByType(task, 'past-due')).length +
            selectedPetAddonTasks.filter(task => filterAddonTaskByType(task, 'past-due')).length;

        const completed =
            selectedPetTasks.filter(task => filterTaskByType(task, 'completed')).length +
            selectedPetAddonTasks.filter(task => filterAddonTaskByType(task, 'completed')).length;

        return { upcoming, pastDue, completed };
    }, [selectedPetTasks, selectedPetAddonTasks]);

    const filteredTasks = useMemo(() => {
        return selectedPetTasks.filter(task => filterTaskByType(task, selectedFilter));
    }, [selectedPetTasks, selectedFilter]);

    const filteredAddonTasks = useMemo(() => {
        return selectedPetAddonTasks.filter(task => filterAddonTaskByType(task, selectedFilter));
    }, [selectedPetAddonTasks, selectedFilter]);

    const filterOptions = useMemo(
        () => [
            {
                id: 'upcoming',
                name: 'Upcoming',
                count: taskCounts.upcoming
            },
            {
                id: 'past-due',
                name: 'Past Due',
                count: taskCounts.pastDue
            },
            {
                id: 'completed',
                name: 'Completed',
                count: taskCounts.completed
            }
        ],
        [taskCounts]
    );

    const handlerCloseTaskComponent = () => {
        setShowTaskComponent(false);
        setTaskToEdit(undefined);
        setSelectedTaskType(undefined);
    };

    const handlerOpenAddTask = () => {
        if (isReadOnly) return;
        setTaskToEdit(undefined);
        setTaskModalKey(prev => prev + 1);
        setShowTaskTypeSelector(true);
    };

    const handlerAddTaskFromSuccess = () => {
        setTaskToEdit(undefined);
        setSelectedTaskType(undefined);
        setShowTaskComponent(false);
        setShowTaskTypeSelector(true);
    };

    const handlerSelectTaskType = (typeId: string) => {
        setSelectedTaskType(typeId);
        setShowTaskTypeSelector(false);
        setShowTaskComponent(true);
    };

    const handleFilterSelect = (filterId: string) => {
        setSelectedFilter(filterId as TaskFilterType);
    };

    const handlerEditTask = (task: Task) => {
        if (isReadOnly) return;
        setTaskToEdit(task);
        setTaskModalKey(prev => prev + 1);
        setShowTaskComponent(true);
    };

    const getMarketplaceName = () => {
        const marketplace = order?.items.appointments[0].appointment.marketplaceId;
        if (marketplace) {
            const marketplaceData = getOrganizationMarketplace(marketplace);
            return marketplaceData?.name || '';
        }
        return '';
    };

    return (
        <div className={clsx(styles.root, className)}>
            {organizationMarketplaces.length > 1 && (
                <div className={clsx(styles.marketplaceContainer, styles.marketplaceContainer)}>
                    <FontAwesomeIcon
                        icon={faLocationDot}
                        className={styles.marketplaceIcon}
                    />
                    <Title
                        title={getMarketplaceName() ? `${getMarketplaceName()}` : ''}
                        className={styles.marketplaceTitle}
                    />
                </div>
            )}
            <PetTagTabs
                pets={pets}
                value={selectedPet}
                onChange={setSelectedPet}
                className={styles.tabs}
            />
            <PetProfileCard
                pet={selectedPet}
                className={clsx(styles.petProfileCard, { [styles.empty]: !hasAnyTasks })}
            />

            {hasAnyTasks && (
                <div className={styles.header}>
                    <span className={styles.title}>Tasks</span>
                    <Button
                        label="+ Add Task"
                        className={styles.addTaskButton}
                        onClick={handlerOpenAddTask}
                        disabled={isReadOnly}
                    />
                </div>
            )}

            {hasAnyTasks && (
                <div className={styles.tabsContainer}>
                    <Tabs
                        value={selectedFilter}
                        tabs={filterOptions.map((tab, index) => ({
                            count: tab.count,
                            id: tab.id,
                            name: tab.name
                        }))}
                        onChange={handleFilterSelect}
                    />
                </div>
            )}

            <TaskDetail
                tasks={filteredTasks}
                addonTasks={filteredAddonTasks}
                className={styles.tasksDetailContainer}
                appointmentItems={order?.items?.appointments || []}
                readOnly={isReadOnly}
                onEdit={task => {
                    if (isReadOnly) return;
                    handlerEditTask(task);
                }}
                empty={!hasAnyTasks}
                onAddTask={handlerOpenAddTask}
            />

            {showTaskTypeSelector && (
                <TaskTypeSelector
                    onSelectType={handlerSelectTaskType}
                    onClose={() => setShowTaskTypeSelector(false)}
                />
            )}

            {showTaskComponent && (
                <TaskComponent
                    key={taskModalKey}
                    petId={selectedPet.id!}
                    task={taskToEdit}
                    appointments={order?.items?.appointments || []}
                    onClose={handlerCloseTaskComponent}
                    onAddTask={handlerAddTaskFromSuccess}
                    defaultTaskType={
                        taskToEdit
                            ? undefined
                            : (selectedTaskType as 'feeding' | 'medication' | 'relief' | 'hydration' | undefined)
                    }
                />
            )}
        </div>
    );
};

export default OrderDetailTasksTab;
